import { __assign, __decorate, __extends, __metadata } from "tslib";
import * as React from 'react';
import { BindPropsHelper } from './PropTypes';
import { FormContext } from './FormContext';
import { Misc, NumberUtils, Utils } from '@singularsystems/neo-core';
import { InputHelpers } from './InputHelpers';
import { observer } from 'mobx-react';
import memoize from 'memoize-one';
import ComponentBase from './ComponentBase';
var FormatState = /** @class */ (function () {
    function FormatState(isPercent, hasExternalPercentSymbol, formatString, 
    /** How many decimals the value is allowed. */
    allowedDecimals, zeroText, useNative) {
        this.isPercent = isPercent;
        this.hasExternalPercentSymbol = hasExternalPercentSymbol;
        this.formatString = formatString;
        this.allowedDecimals = allowedDecimals;
        this.zeroText = zeroText;
        this.useNative = useNative;
    }
    FormatState.create = function (propertyInfo, numericType, numProps, appendText) {
        var isPercent = false;
        var hasExternalPercentSymbol = false;
        var formatString = numProps ? numProps.formatString : "";
        var displayDecimals = 2;
        if ((formatString && formatString.indexOf("%") >= 0)
            || appendText === "%"
            || (numProps === null || numProps === void 0 ? void 0 : numProps.format) === Misc.NumberFormat.PercentDecimals || (numProps === null || numProps === void 0 ? void 0 : numProps.format) === Misc.NumberFormat.PercentNoDecimals) {
            isPercent = true;
        }
        if (propertyInfo.allowedDecimals !== undefined) {
            displayDecimals = propertyInfo.allowedDecimals - (isPercent ? 2 : 0);
        }
        else if (numProps === null || numProps === void 0 ? void 0 : numProps.formatString) {
            displayDecimals = NumberUtils.getDecimalPlaces(numProps.formatString);
        }
        if (!formatString) {
            if (numProps && numProps.format) {
                formatString = NumberUtils.getFormatString(numProps.format, numProps.currencySymbol, displayDecimals);
            }
            else {
                var hasDecimals = numericType === Misc.DataType.Float || numericType === Misc.DataType.Decimal;
                var format = (numProps === null || numProps === void 0 ? void 0 : numProps.currencySymbol) ?
                    (hasDecimals ? Misc.NumberFormat.CurrencyDecimals : Misc.NumberFormat.CurrencyNoDecimals) :
                    (hasDecimals ? Misc.NumberFormat.Decimals : Misc.NumberFormat.NoDecimals);
                formatString = NumberUtils.getFormatString(format, numProps === null || numProps === void 0 ? void 0 : numProps.currencySymbol, displayDecimals);
            }
        }
        if (formatString.indexOf("%") < 0 && appendText === "%") {
            hasExternalPercentSymbol = true;
        }
        return new FormatState(isPercent, hasExternalPercentSymbol, formatString, displayDecimals + (isPercent ? 2 : 0), numProps === null || numProps === void 0 ? void 0 : numProps.zeroText, numProps === null || numProps === void 0 ? void 0 : numProps.native);
    };
    FormatState.prototype.getFormattedValue = function (value) {
        if (!value && this.zeroText !== undefined) {
            return this.zeroText;
        }
        if (value !== undefined && value !== null) {
            value = NumberUtils.decimalService.toNumber(value);
            if (typeof value === "number") {
                if (this.hasExternalPercentSymbol) {
                    value *= 100;
                }
                return this.useNative ? NumberUtils.formatRaw(value, this.formatString, false) : NumberUtils.format(value, this.formatString);
            }
            else {
                return value;
            }
        }
        else {
            return "";
        }
    };
    return FormatState;
}());
export { FormatState };
// Taken from https://github.com/MikeMcl/decimal.js-light/blob/master/decimal.js since it is not exported.
var validDecimalRegex = /^(\d+(\.\d*)?|\.\d+)(e[+-]?\d+)?$/i;
var NumberInput = /** @class */ (function (_super) {
    __extends(NumberInput, _super);
    function NumberInput(props) {
        var _this = _super.call(this, props) || this;
        _this.hasFocus = false;
        _this.isReadOnly = false;
        _this.recalcFormatState = memoize(FormatState.create);
        _this.numericType = _this.props.bind.propertyInfo.propertyType;
        _this.inputElement = React.createRef();
        _this.onChanged = _this.onChanged.bind(_this);
        _this.onFocus = _this.onFocus.bind(_this);
        _this.onBlur = _this.onBlur.bind(_this);
        _this.onKeyDown = _this.onKeyDown.bind(_this);
        return _this;
    }
    NumberInput.prototype.onChanged = function (e) {
        var newValue;
        var stringValue = e.target.value;
        if (stringValue !== "") {
            if (NumberUtils.allowAsDecimalSeparator) {
                var firstIndex = stringValue.indexOf(NumberUtils.allowAsDecimalSeparator), lastIndex = stringValue.lastIndexOf(NumberUtils.allowAsDecimalSeparator);
                if (firstIndex >= 0 && firstIndex === lastIndex) {
                    stringValue = stringValue.replace(NumberUtils.allowAsDecimalSeparator, ".");
                }
            }
            var testValue = stringValue;
            if (testValue[0] === "-") {
                testValue = testValue.substring(1);
            }
            if (isNaN(parseFloat(testValue)) || !validDecimalRegex.test(testValue)) {
                return;
            }
        }
        var decimal = NumberUtils.decimalService;
        if (stringValue == "") {
            newValue = this.props.bind.propertyInfo.allowNulls ? null : decimal.createDecimal(0);
        }
        else if (this.formatState.isPercent && NumberUtils.transformPercent) {
            newValue = decimal.divideBy(decimal.createDecimal(stringValue), 100);
        }
        else if (this.numericType === Misc.DataType.Float || this.numericType === Misc.DataType.Decimal) {
            newValue = decimal.createDecimal(stringValue);
        }
        else {
            newValue = decimal.truncate(decimal.createDecimal(stringValue));
        }
        if (newValue === null) {
            this.props.bind.value = newValue;
        }
        else {
            if (!this.props.numProps || this.props.numProps.autoRound !== false) {
                newValue = NumberUtils.round(newValue, this.formatState.allowedDecimals);
            }
            if (this.numericType === Misc.DataType.Decimal) {
                this.props.bind.value = newValue;
            }
            else {
                this.props.bind.value = decimal.toNumber(newValue);
            }
        }
    };
    NumberInput.prototype.onFocus = function () {
        var element = this.inputElement.current;
        if (element && !element.disabled) {
            this.setFocusedText(element);
            element.select();
        }
    };
    NumberInput.prototype.getBindValueAsNumber = function () {
        return NumberUtils.decimalService.toNumber(this.props.bind.value);
    };
    NumberInput.prototype.setFocusedText = function (element) {
        this.hasFocus = true;
        var numberValue = this.getBindValueAsNumber();
        var stringValue;
        if (numberValue === null || numberValue === undefined) {
            stringValue = "";
        }
        else {
            if (element.readOnly || !this.props.numProps || this.props.numProps.autoRound !== false) {
                stringValue = NumberUtils.formatRaw(numberValue, this.formatState.formatString, this.formatState.hasExternalPercentSymbol, this.formatState.allowedDecimals);
            }
            else {
                if (this.formatState.isPercent && NumberUtils.transformPercent) {
                    numberValue *= 100;
                }
                stringValue = numberValue.toString();
            }
        }
        element.value = stringValue;
    };
    NumberInput.prototype.onBlur = function (e) {
        this.hasFocus = false;
        if (this.inputElement.current) {
            this.inputElement.current.value = this.formatState.getFormattedValue(this.props.bind.value);
        }
    };
    NumberInput.prototype.onKeyDown = function (e) {
        var element = this.inputElement.current;
        if (!this.isReadOnly && element && (e.keyCode === 38 || e.keyCode === 40)) {
            var value = this.getBindValueAsNumber();
            if (value !== null) {
                var selectionIndex = element.selectionEnd;
                var deltaValue = 1;
                var decimalIndex = element.value.indexOf(".");
                if (decimalIndex < 0) {
                    decimalIndex = element.value.indexOf(",");
                }
                if (decimalIndex > 0 && selectionIndex && selectionIndex > decimalIndex) {
                    deltaValue = 0.01;
                }
                if (this.formatState.isPercent && NumberUtils.transformPercent) {
                    deltaValue /= 100;
                }
                if (e.keyCode === 38) {
                    // Up
                    value += deltaValue;
                }
                else if (e.keyCode === 40) {
                    // Down
                    value -= deltaValue;
                }
                if (this.numericType === Misc.DataType.Decimal) {
                    this.props.bind.value = NumberUtils.decimalService.createDecimal(value);
                }
                else {
                    this.props.bind.value = value;
                }
                this.setFocusedText(element);
                if (selectionIndex !== null) {
                    element.selectionStart = element.selectionEnd = selectionIndex;
                }
                e.preventDefault();
            }
        }
    };
    NumberInput.prototype.componentDidMount = function () {
        if (this.inputElement.current) {
            this.inputElement.current.addEventListener("keydown", this.onKeyDown);
        }
    };
    NumberInput.prototype.componentWillUnmount = function () {
        if (this.inputElement.current) {
            this.inputElement.current.removeEventListener("keydown", this.onKeyDown);
        }
    };
    NumberInput.prototype.render = function () {
        var _a;
        var splitProps = BindPropsHelper.normaliseEditorProps(this, this.props);
        var domProps = BindPropsHelper.autoSetDomProperties(splitProps, this.context, this.onBlur, this.onFocus);
        domProps.className = Utils.joinWithSpaces(domProps.className, splitProps.isBootstrap ? "form-control" : "neo-forms-editor", "numeric-input");
        var editorProps = splitProps.editor;
        var useNative = (_a = splitProps.numProps) === null || _a === void 0 ? void 0 : _a.native;
        var displayInfo = this.props.bind.validator.getDisplayState(this.context.validationDisplayMode);
        displayInfo.applyToDom(domProps, !splitProps.inGroup);
        this.formatState = this.recalcFormatState(this.props.bind.propertyInfo, this.numericType, splitProps.numProps, editorProps.appendText);
        this.isReadOnly = domProps.readOnly === true;
        if (editorProps.inputElement) {
            this.inputElement = editorProps.inputElement;
        }
        // Don't update the elements value if it is focused
        if (!this.hasFocus) {
            if (this.inputElement.current) {
                this.inputElement.current.value = this.formatState.getFormattedValue(editorProps.bind.value);
            }
        }
        if (useNative && !domProps.step) {
            var decimals = NumberUtils.getDecimalPlaces(this.formatState.formatString);
            domProps.step = decimals === 0 ? "1" : "0.".concat("0".repeat(decimals - 1), "1");
        }
        return (InputHelpers.surroundWithInputGroup(React.createElement("input", __assign({}, domProps, { defaultValue: this.formatState.getFormattedValue(editorProps.bind.value), type: useNative ? "number" : "text", onChange: this.onChanged, ref: this.inputElement })), editorProps, this.inputElement));
    };
    NumberInput.contextType = FormContext;
    NumberInput = __decorate([
        observer,
        __metadata("design:paramtypes", [Object])
    ], NumberInput);
    return NumberInput;
}(ComponentBase));
export default NumberInput;
